<script setup lang="ts">
import type { Testimonial } from '~/types'

const { data: stats, pending } = await useFetch('/api/public/coach-stats')

const { data } = await useFetch('/api/testimonials')
const testimonials = computed(() => (data.value?.testimonials || []) as Testimonial[])
</script>

<template>
  <section
    class="border-t border-slate-200 bg-gradient-to-b from-white to-slate-50 dark:border-slate-800 dark:from-slate-950 dark:to-slate-900"
  >
    <UContainer class="py-16 md:py-24">
      <!-- Titolo sezione -->
      <div class="text-center mb-12">
        <h2 class="text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
          I Risultati Parlano Chiaro
        </h2>
        <p class="mt-4 text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
          Numeri concreti che testimoniano l'impegno e i progressi dei miei atleti
        </p>
      </div>
      <!-- Stats Grid -->
      <div v-if="!pending && stats" class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
        <!-- Gare Totali -->
        <div
          class="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:shadow-lg dark:border-slate-800 dark:bg-slate-900"
        >
          <div
            class="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-primary-500/10 transition-transform group-hover:scale-110"
          />
          <div class="relative">
            <div class="flex items-center justify-between mb-4">
              <div class="rounded-lg bg-primary-500/10 p-3">
                <UIcon name="i-lucide-trophy" class="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
            <div class="text-3xl font-bold text-slate-900 dark:text-white">
              {{ stats.totalRaces }}
            </div>
            <div class="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">
              Gare Totali
            </div>
            <div class="mt-1 text-xs text-slate-500 dark:text-slate-500">
              Da sempre
            </div>
          </div>
        </div>

        <!-- Gare Passate Anno Corrente -->
        <div
          class="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:shadow-lg dark:border-slate-800 dark:bg-slate-900"
        >
          <div
            class="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-green-500/10 transition-transform group-hover:scale-110"
          />
          <div class="relative">
            <div class="flex items-center justify-between mb-4">
              <div class="rounded-lg bg-green-500/10 p-3">
                <UIcon name="i-lucide-medal" class="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div class="text-3xl font-bold text-slate-900 dark:text-white">
              {{ stats.pastRacesThisYear }}
            </div>
            <div class="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">
              Gare Completate
            </div>
            <div class="mt-1 text-xs text-slate-500 dark:text-slate-500">
              Nel {{ stats.currentYear }}
            </div>
          </div>
        </div>

        <!-- Gare Totali Anno Corrente -->
        <div
          class="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:shadow-lg dark:border-slate-800 dark:bg-slate-900"
        >
          <div
            class="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-blue-500/10 transition-transform group-hover:scale-110"
          />
          <div class="relative">
            <div class="flex items-center justify-between mb-4">
              <div class="rounded-lg bg-blue-500/10 p-3">
                <UIcon name="i-lucide-calendar-days" class="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div class="text-3xl font-bold text-slate-900 dark:text-white">
              {{ stats.totalRacesThisYear }}
            </div>
            <div class="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">
              Gare Programmate
            </div>
            <div class="mt-1 text-xs text-slate-500 dark:text-slate-500">
              Totali {{ stats.currentYear }}
            </div>
          </div>
        </div>

        <!-- Test Totali -->
        <div
          class="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:shadow-lg dark:border-slate-800 dark:bg-slate-900"
        >
          <div
            class="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-purple-500/10 transition-transform group-hover:scale-110"
          />
          <div class="relative">
            <div class="flex items-center justify-between mb-4">
              <div class="rounded-lg bg-purple-500/10 p-3">
                <UIcon name="i-lucide-activity" class="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <div class="text-3xl font-bold text-slate-900 dark:text-white">
              {{ stats.totalTests }}
            </div>
            <div class="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">
              Test Svolti
            </div>
            <div class="mt-1 text-xs text-slate-500 dark:text-slate-500">
              Totali
            </div>
          </div>
        </div>

        <!-- Miglioramento Medio -->
        <div
          class="group relative overflow-hidden rounded-2xl border p-6 transition-all hover:shadow-lg"
          :class="stats.avgTestImprovement > 0
            ? 'border-green-200 bg-gradient-to-br from-green-50 to-white dark:border-green-800 dark:from-green-950/30 dark:to-slate-900'
            : stats.avgTestImprovement < 0
              ? 'border-red-200 bg-gradient-to-br from-red-50 to-white dark:border-red-800 dark:from-red-950/30 dark:to-slate-900'
              : 'border-orange-200 bg-gradient-to-br from-orange-50 to-white dark:border-orange-800 dark:from-orange-950/30 dark:to-slate-900'
          "
        >
          <div
            class="absolute -right-4 -top-4 h-24 w-24 rounded-full transition-transform group-hover:scale-110"
            :class="stats.avgTestImprovement > 0 ? 'bg-green-500/10' : stats.avgTestImprovement < 0 ? 'bg-red-500/10' : 'bg-orange-500/10'"
          />
          <div class="relative">
            <div class="flex items-center justify-between mb-4">
              <div
                class="rounded-lg p-3"
                :class="stats.avgTestImprovement > 0 ? 'bg-green-500/10' : stats.avgTestImprovement < 0 ? 'bg-red-500/10' : 'bg-orange-500/10'"
              >
                <UIcon
                  :name="stats.avgTestImprovement > 0 ? 'i-lucide-trending-up' : stats.avgTestImprovement < 0 ? 'i-lucide-trending-down' : 'i-lucide-minus'"
                  class="h-6 w-6"
                  :class="stats.avgTestImprovement > 0 ? 'text-green-600 dark:text-green-400' : stats.avgTestImprovement < 0 ? 'text-red-600 dark:text-red-400' : 'text-orange-600 dark:text-orange-400'"
                />
              </div>
            </div>
            <div
              class="text-3xl font-bold"
              :class="stats.avgTestImprovement > 0 ? 'text-green-700 dark:text-green-400' : stats.avgTestImprovement < 0 ? 'text-red-700 dark:text-red-400' : 'text-orange-700 dark:text-orange-400'"
            >
              {{ stats.avgTestImprovement > 0 ? '+' : '' }}{{ stats.avgTestImprovement }}%
            </div>
            <div class="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">
              Miglioramento Medio
            </div>
            <div class="mt-1 text-xs text-slate-500 dark:text-slate-500">
              FTP tra test consecutivi
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="pending" class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
        <div v-for="i in 5" :key="i" class="h-40 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      </div>

      <!-- CTA sotto le statistiche -->
      <div class="mt-12 text-center">
        <p class="text-slate-600 dark:text-slate-400 mb-6">
          Questi numeri rappresentano solo una parte del lavoro. Vuoi far parte di questa storia?
        </p>
        <UButton
          to="#pacchetti"
          size="lg"
          color="primary"
          class="shadow-lg hover:shadow-xl transition-shadow"
        >
          Scopri i Pacchetti
          <template #trailing>
            <UIcon name="i-lucide-arrow-right" class="h-5 w-5" />
          </template>
        </UButton>
      </div>
    </UContainer>
    <UContainer class="-py-1 md:-py-1">
      <h2 class="text-2xl font-semibold text-slate-900 dark:text-white md:text-3xl">
        Testimonianze
      </h2>
      <p class="mt-3 max-w-2xl text-slate-600 dark:text-slate-300">
        Feedback chiaro, progressioni sostenibili, obiettivi concreti.
      </p>
      <UCarousel
        v-slot="{ item }"
        :items="testimonials"
        arrows
        :ui="{
          item: 'basis-full md:basis-1/2 lg:basis-1/3 px-2 ml-1',
          container: 'py-7'
        }"
        class="w-full"
      >
        <UCard class="rounded-3xl border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950 h-full">
          <template #header>
            <p class="text-sm font-semibold text-slate-900 dark:text-white">
              {{ (item as Testimonial).name }}
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ (item as Testimonial).sport }}
            </p>
          </template>
          <p class="text-sm text-slate-700 dark:text-slate-200 italic">
            “{{ (item as Testimonial).comment }}”
          </p>
        </UCard>
      </UCarousel>
    </UContainer>
  </section>
</template>
